/**
 * Session Management Configuration Constants
 *
 * Centralized constants for Auth0 and OIDC session management.
 * These values MUST be consistent across all hooks to ensure
 * activity tracking and dialog display work correctly together.
 */

// ============================================================================
// TIMING CONSTANTS
// ============================================================================

/**
 * Warning threshold - show dialog when this many ms remain before token expiry
 * Production value — 2 minutes before token expiry
 */
export const WARNING_THRESHOLD_MS = 2 * 60 * 1000; // 2 minutes

/**
 * Debounce duration for activity tracking
 * Prevents rapid localStorage writes on every mouse movement
 */
export const ACTIVITY_DEBOUNCE_MS = 30000; // 30 seconds

/**
 * Minimum interval between token refresh attempts (rate limiting)
 */
export const MIN_REFRESH_INTERVAL_MS = 5000; // 5 seconds

/**
 * Check interval for session timeout dialog
 */
export const DIALOG_CHECK_INTERVAL_MS = 10000; // 10 seconds

/**
 * OIDC token refresh timeout - max time to wait for signinSilent
 */
export const OIDC_REFRESH_TIMEOUT_MS = 10000; // 10 seconds

/**
 * Auth0 token refresh timeout - max time to wait for getAccessTokenSilently
 */
export const AUTH0_REFRESH_TIMEOUT_MS = 10000; // 10 seconds

/**
 * Follower wait timeout - max time non-leader tabs wait for leader to refresh
 * Used in Promise barrier pattern to prevent indefinite waiting
 */
export const FOLLOWER_WAIT_TIMEOUT_MS = 10000; // 10 seconds

/**
 * Leeway added to warning threshold when calculating refresh timing
 * Ensures refresh happens slightly before the warning dialog would appear
 * NOTE: Only used by OIDC flow. Auth0 uses AUTH0_SDK_LEEWAY_MS instead.
 */
export const REFRESH_LEEWAY_MS = 60000; // 60 seconds (1 minute)

/**
 * One year in milliseconds - used for checks on timestamp validity
 */
export const ONE_YEAR_MS = 365 * 24 * 60 * 60 * 1000;

// ============================================================================
// AUTH0-SPECIFIC CONSTANTS
// ============================================================================
// Auth0 SPA SDK has a hardcoded 60-second leeway that prevents token refresh
// until the token has ≤60 seconds remaining. These constants align our
// scheduling logic with the SDK's internal behavior to prevent infinite loops.

/**
 * Auth0 SDK internal leeway - the SDK won't refresh tokens with more than
 * this many ms remaining. This is hardcoded in auth0-spa-js and cannot be changed.
 * @see https://github.com/auth0/auth0-spa-js/issues/1032
 */
export const AUTH0_SDK_LEEWAY_MS = 60 * 1000; // 60 seconds

/**
 * Safety buffer inside Auth0's refresh window.
 * We schedule refresh at (expiry - SDK_LEEWAY + BUFFER) = 55s before expiry.
 * This ensures we're safely inside the SDK's refresh window, not at the edge.
 */
export const AUTH0_REFRESH_BUFFER_MS = 5 * 1000; // 5 seconds

/**
 * Backoff delay when Auth0 SDK returns the same token (hasn't actually refreshed).
 * Instead of immediately retrying (which causes infinite loop), wait this long.
 */
export const AUTH0_BACKOFF_DELAY_MS = 10 * 1000; // 10 seconds

/**
 * Debounce delay for @@auth0spajs@@ key removal detection.
 * Allows time for SDK cache rotation (remove → set) to complete
 * and for BroadcastChannel LOGOUT_REQUESTED to arrive first.
 */
export const STORAGE_KEY_DEBOUNCE_MS = 2000; // 2 seconds

/**
 * Extended debounce when a token refresh is in progress.
 * The SDK may take longer during the network-bound refresh token exchange.
 * Must exceed AUTH0_REFRESH_TIMEOUT_MS (10s) to avoid false logout during slow refreshes.
 */
export const STORAGE_KEY_DEBOUNCE_REFRESHING_MS = 12000; // 12 seconds

/**
 * Grace period after user clicks "Continue Working" in the timeout dialog.
 * During this period, the dialog won't reopen even if user appears inactive.
 * This prevents jarring rapid dialog re-shows due to state sync delays.
 * Using timestamp-based check (Date.now() - continueTime < GRACE_MS) instead of
 * setTimeout for reliability - timeouts can be lost due to tab throttling.
 */
export const JUST_CONTINUED_GRACE_MS = 10 * 1000; // 10 seconds

/**
 * Maximum time a token can be expired before we force-logout, even if the
 * user appears active. Prevents infinite refresh-request loops when a
 * follower's activity state is stale (e.g., missed TOKEN_REFRESHED broadcast).
 */
export const EXPIRED_SAFETY_NET_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Clock skew tolerance for activity comparison with token IAT.
 * Handles minor drift between client and server clocks.
 * The justContinued guard handles grace periods separately.
 */
export const CLOCK_SKEW_TOLERANCE_MS = 5 * 1000; // 5 seconds

/**
 * Tolerance for detecting stale activity from previous sessions.
 * If activity timestamp is older than token IAT minus this tolerance,
 * it's considered stale and should be reset.
 */
export const STALE_ACTIVITY_TOLERANCE_MS = 60 * 1000; // 60 seconds

// ============================================================================
// RETRY/BACKOFF CONSTANTS
// ============================================================================

/**
 * Maximum retry attempts for transient errors
 */
export const MAX_RETRIES = 5;

/**
 * Base delay for exponential backoff
 */
export const BACKOFF_BASE_MS = 100;

/**
 * Maximum delay for exponential backoff
 */
export const BACKOFF_MAX_MS = 60000; // 60 seconds

/**
 * Jitter factor for backoff (±20%)
 */
export const BACKOFF_JITTER = 0.2;

// ============================================================================
// TERMINAL ERRORS
// ============================================================================

/**
 * Auth errors that should NOT be retried.
 * These indicate the session is permanently invalid.
 * Covers Auth0 OAuth error codes AND Firebase Auth error codes.
 */
export const TERMINAL_ERRORS = new Set([
  // Auth0 / OAuth
  'invalid_grant',
  'login_required',
  'consent_required',
  'interaction_required',
  'invalid_authorization_code',
  'mfa_required',
  'mfa_enroll', // Auth0 returns this when MFA enrollment is required
  'access_denied', // User denied consent or was blocked
  'unauthorized_client', // Client not authorized for this grant type
  // Firebase Auth (error.code values from firebase/auth SDK)
  'auth/user-disabled',
  'auth/user-not-found',
  'auth/invalid-credential',
  'auth/invalid-user-token',
  'auth/user-token-expired',
]);

/**
 * Check if an error is terminal (should not be retried).
 * Handles Auth0 OAuth errors, OIDC errors, and Firebase Auth errors.
 * @param {Error} error - The error from the auth SDK
 * @returns {boolean} True if error is terminal
 */
export const isTerminalError = (error) => {
  if (!error) return false;

  // Auth0 SDK errors have an `error` property with the OAuth error code.
  // Firebase errors have a `code` property (e.g. "auth/user-disabled").
  // Standard JS errors have `message` and sometimes `code`.
  const errorCode = error.error || error.code;

  if (errorCode && TERMINAL_ERRORS.has(errorCode)) {
    return true;
  }

  // Also check error_description / message for terminal indicators
  const description = error.error_description || error.message || '';
  const descLower = description.toLowerCase();

  if (
    descLower.includes('invalid refresh token') ||
    descLower.includes('refresh token is expired') ||
    descLower.includes('user is blocked') ||
    descLower.includes('grant type not allowed') ||
    descLower.includes('unknown or invalid refresh token') ||
    descLower.includes('user is disabled') ||
    descLower.includes('token has been revoked')
  ) {
    return true;
  }

  return false;
};

// ============================================================================
// STORAGE KEYS (shared across providers - activity/refresh are provider-agnostic)
// ============================================================================

export const STORAGE_KEYS = {
  ACTIVITY: 'bb_last_activity_timestamp',
  REFRESH: 'bb_last_refresh_timestamp',
  DIALOG_OPEN: 'bb_timeout_dialog_open',
  AUTH_CONFIG: 'bb_auth_config', // Stores auth config for satellite apps
  OIDC_REDIRECT: 'bb_oidc_redirect', // SessionStorage key for OIDC redirect URL
  AUTH0_REDIRECT: 'bb_auth0_redirect', // SessionStorage key for Auth0 redirect URL (fallback for lost appState)
  POST_LOGIN_REDIRECT: 'bb_post_login_redirect', // SessionStorage key for deep-link redirect after login
  PERMISSIONS: 'bb_user_permissions', // Cached user permissions
  PERMISSIONS_TS: 'bb_user_permissions_ts', // Timestamp of cached permissions
  GCP_TOKEN: 'bb.gcp.access_token', // Firebase ID token for GCP provider
};

// ============================================================================
// BROADCAST CHANNEL NAMES
// ============================================================================

export const CHANNEL_NAMES = {
  // Auth0 channels
  SESSION: 'bb_auth0_session',
  ACTIVITY: 'bb_auth0_activity',
  // OIDC channels (separate namespace to avoid conflicts)
  OIDC_SESSION: 'bb_oidc_session',
  OIDC_ACTIVITY: 'bb_oidc_activity',
  // Cross-tab auth sync (login/logout/permission changes)
  AUTH_SYNC: 'bb_auth_sync_v1',
};

// ============================================================================
// WEB LOCK NAMES (for leader election)
// ============================================================================

export const LOCK_NAMES = {
  AUTH0: 'bb_auth0_session_leader',
  OIDC: 'bb_oidc_session_leader',
};

// ============================================================================
// MESSAGE TYPES
// ============================================================================

export const MESSAGE_TYPES = {
  // Token refresh
  TOKEN_REFRESHED: 'TOKEN_REFRESHED',
  REFRESH_REQUESTED: 'REFRESH_REQUESTED',

  // Dialog state
  DIALOG_SHOWN: 'DIALOG_SHOWN',
  DIALOG_DISMISSED: 'DIALOG_DISMISSED',

  // Activity
  ACTIVITY_DETECTED: 'ACTIVITY_DETECTED',
  SESSION_RESET: 'SESSION_RESET',

  // Logout
  LOGOUT_REQUESTED: 'LOGOUT_REQUESTED',
};
